import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { WorldMap } from '../components/dashboard/WorldMap';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtV = (v) => v >= 1e9 ? '$' + (v / 1e9).toFixed(2) + 'B' : (v >= 1e6 ? '$' + (v / 1e6).toFixed(2) + 'M' : (v >= 1e3 ? '$' + (v / 1e3).toFixed(0) + 'k' : '$' + Math.round(v)));
const fmtQ = (k) => k >= 1e6 ? (k / 1e6).toFixed(1) + 'M t' : (k >= 1000 ? (k / 1000).toFixed(1) + 'k t' : k.toFixed(0) + ' kg');

const GUIDE = {
  sections: [
    { title: 'World Map', desc: 'Each bubble is a buyer country, sized by total import value. Click a country to filter buyer cards to that market only.', example: 'Example: The US bubble is the largest ($4.56B). Click it to see only US-based buyers.' },
    { title: 'Buyer Cards', desc: 'Top 200 buyers sorted by import value (largest first). Each card shows total import value, volume, shipment count, and supplier concentration gauge.', example: 'Example: AZ Gems Inc shows $425M with a 5% gauge — meaning their top supplier holds only 5%.' },
    { title: 'Concentration Gauge', desc: 'Shows how much of that buyer\'s imports come from their single largest supplier. Low % = buyer spreads spend. High % = depends on one supplier.', example: 'Example: A 50% gauge means half their imports go to one exporter.' },
    { title: 'Sort & Search', desc: 'Toggle "By Value $", "By Volume", or "By Suppliers" to re-rank cards. Use search to find specific buyers.', example: 'Example: Sort by "Suppliers" to find buyers with only 1-2 suppliers.' },
  ],
  lookFor: 'High-value buyers with low supplier concentration (coral badge, low %) = diversified accounts open to new suppliers. "SINGLE SOURCE" badge = risky for them, opportunity for Sharat to become backup.',
};

/* ── Concentration Gauge (simpler version for buyer cards) ── */
const ConcentrationGauge = ({ pct, nSups }) => {
  const color = nSups <= 1 ? '#8FD694' : nSups <= 3 ? '#F2B14C' : '#FF6B52';
  const R = 30;
  const circ = 2 * Math.PI * R;
  const offset = circ * (1 - Math.min(1, pct / 100));

  return (
    <svg viewBox="0 0 76 76" width={68} height={68} className="shrink-0">
      <circle cx="38" cy="38" r={R} fill="none" stroke="var(--dash-panel2)" strokeWidth={8} />
      <circle cx="38" cy="38" r={R} fill="none" stroke={color} strokeWidth={8} strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={offset} transform="rotate(-90 38 38)" />
      <text x="38" y="35" textAnchor="middle" fontFamily="Hanken Grotesk" fontWeight="700" fontSize="14" fill="var(--dash-ink)">{pct}%</text>
      <text x="38" y="48" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="7" fill="var(--dash-mut)">BY $</text>
    </svg>
  );
};

/* ── Buyer Card ── */
const BuyerCard = ({ buyer }) => {
  const badgeClass = buyer.nSups <= 1
    ? 'bg-[rgba(143,214,148,0.12)] text-[#8FD694]'
    : buyer.nSups <= 3
      ? 'bg-[rgba(242,177,76,0.12)] text-dash-amber'
      : 'bg-[rgba(255,107,82,0.12)] text-dash-coral';
  const badgeText = buyer.nSups <= 1 ? 'SINGLE SOURCE' : `${buyer.nSups} SUPPLIERS`;

  return (
    <div className="bg-dash-panel border border-dash-line rounded-[14px] p-4 transition hover:border-dash-ice" data-testid={`buyer-card-${buyer.name}`}>
      <div className="font-mono text-[10px] text-dash-mut uppercase tracking-[0.1em] mb-1">{buyer.country}</div>
      <div className="font-grotesk font-bold text-[15px] mb-2.5 whitespace-nowrap overflow-hidden text-ellipsis" title={buyer.name}>{buyer.name}</div>
      <div className="flex items-center gap-3.5">
        <ConcentrationGauge pct={buyer.shareVal} nSups={buyer.nSups} />
        <div className="text-xs text-dash-mut leading-relaxed">
          Imports: <b className="text-dash-ink">{fmtV(buyer.val)}</b><br />
          {fmtQ(buyer.qty)} &middot; {buyer.shp} shp<br />
          <span className="text-dash-ice text-[11px]">Top: {buyer.topSup}</span>
        </div>
      </div>
      <span className={`inline-block font-mono text-[9px] tracking-[0.08em] uppercase font-bold px-2 py-1 rounded-[5px] mt-2.5 ${badgeClass}`}>
        {badgeText}
      </span>
    </div>
  );
};

/* ── Skeleton for Buyers Explorer ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

/* ── Single buyer-card skeleton — used both for the initial page skeleton and the
   infinite-scroll "loading more rows" placeholder ── */
const BuyerCardSkeleton = ({ delay = 0 }) => (
  <div className="rounded-[14px] p-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
    <SB className="h-2 w-16 mb-1.5" delay={delay} />
    <SB className="h-3.5 w-[65%] mb-3" delay={delay + 20} />
    <div className="flex items-center gap-3.5">
      <div className="skel-bone skel-round shrink-0" style={{ width: 68, height: 68, animationDelay: `${delay + 40}ms` }} />
      <div className="flex-1 space-y-2">
        <SB className="h-2.5 w-[75%]" delay={delay + 60} />
        <SB className="h-2 w-[50%]" delay={delay + 80} />
        <SB className="h-2 w-[60%]" delay={delay + 100} />
      </div>
    </div>
    <SB className="h-4 w-28 mt-2.5 !rounded-[5px]" delay={delay + 120} />
  </div>
);

const BuyersSkeleton = () => (
  <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="buyers-skeleton">
    <div className="max-w-[1340px] mx-auto">
      <SB className="h-2 w-32 mb-2" delay={0} />
      <SB className="h-6 w-96 mb-1.5" delay={30} />
      <SB className="h-3 w-80 mb-4" delay={60} />

      {/* 4 KPI cards */}
      <div className="grid grid-cols-4 gap-3.5 mb-5">
        {[0,1,2,3].map(i => (
          <div key={i} className="rounded-xl p-4 text-center" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
            <SB className="h-2 w-16 mx-auto mb-2" delay={80 + i * 30} />
            <SB className="h-7 w-20 mx-auto" delay={100 + i * 30} />
          </div>
        ))}
      </div>

      {/* Map placeholder */}
      <div className="rounded-[14px] p-5 mb-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
        <SB className="h-4 w-40 mb-1" delay={200} />
        <SB className="h-2.5 w-72 mb-3" delay={220} />
        <div className="relative h-[250px] overflow-hidden rounded-[10px]" style={{ background: 'var(--dash-bg)' }}>
          {[
            { x: '22%', y: '28%', s: 44 }, { x: '50%', y: '22%', s: 28 },
            { x: '76%', y: '34%', s: 36 }, { x: '60%', y: '44%', s: 18 },
            { x: '30%', y: '54%', s: 14 }, { x: '84%', y: '58%', s: 22 },
          ].map((b, i) => (
            <div key={i} className="skel-bone skel-round absolute" style={{ left: b.x, top: b.y, width: b.s, height: b.s, animationDelay: `${240 + i * 50}ms` }} />
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <SB className="h-9 w-72 !rounded-lg" delay={500} />
        <div className="flex gap-1 ml-auto">
          <SB className="h-8 w-20 !rounded-md" delay={520} />
          <SB className="h-8 w-24 !rounded-md" delay={540} />
          <SB className="h-8 w-24 !rounded-md" delay={560} />
        </div>
      </div>

      {/* Buyer cards grid - 3 rows */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-3.5">
        {Array.from({ length: 9 }, (_, i) => (
          <BuyerCardSkeleton key={i} delay={580 + i * 30} />
        ))}
      </div>
    </div>
  </div>
);

/* ── Main Page ── */
export default function BuyersExplorerReact() {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortMode, setSortMode] = useState('val');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState(null);

  const [months, setMonths] = useState([]);
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [appliedRange, setAppliedRange] = useState([0, 0]);
  const [isFiltered, setIsFiltered] = useState(false);

  // Infinite scroll (3 rows visible) for the buyer card grid — same pattern as Competitive Intelligence's
  // customer panel. The grid is `repeat(auto-fill, minmax(320px,1fr))`, so the number of cards per row
  // depends on viewport width; colsPerRow is measured from the browser's resolved grid-template-columns
  // rather than assumed, so "3 rows" holds at any width.
  const BUYER_ROWS_VISIBLE = 3;
  const [colsPerRow, setColsPerRow] = useState(3);
  const [visibleBuyerCount, setVisibleBuyerCount] = useState(9);
  const [buyerScrollH, setBuyerScrollH] = useState(null);
  const [buyerLoadingMore, setBuyerLoadingMore] = useState(false);
  const buyerScrollRef = useRef(null);
  const buyerGridRef = useRef(null);
  const buyerSentinelRef = useRef(null);
  const buyerLoadTimeoutRef = useRef(null);
  const buyerIsLoadingRef = useRef(false);
  const buyerPageSize = Math.max(colsPerRow, 1) * BUYER_ROWS_VISIBLE;

  useEffect(() => {
    fetch(`${API}/api/static/buyers_explorer.json`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        setRawData(d);
        const mm = d.L?.month || [];
        const last = Math.max(0, mm.length - 1);
        setMonths(mm);
        setToIdx(last);
        setAppliedRange([0, last]);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const applyPeriod = useCallback(() => {
    if (fromIdx > toIdx) { alert('From must be before To'); return; }
    setAppliedRange([fromIdx, toIdx]);
    setIsFiltered(true);
  }, [fromIdx, toIdx]);

  const resetPeriod = useCallback(() => {
    const last = Math.max(0, months.length - 1);
    setFromIdx(0); setToIdx(last);
    setAppliedRange([0, last]);
    setIsFiltered(false);
  }, [months]);

  // Aggregate rows into buyer objects, restricted to the applied month range
  const { buyers, totalVal, totalQty, countries, countryData } = useMemo(() => {
    if (!rawData) return { buyers: [], totalVal: 0, totalQty: 0, countries: 0, countryData: [] };
    const L = rawData.L;
    const rows = rawData.rows.filter(r => r[0] >= appliedRange[0] && r[0] <= appliedRange[1]);

    // Aggregate by buyer
    const agg = {};
    rows.forEach(r => {
      // [month_idx, buyer_idx, country_idx, val, qty, shp, top_supplier_idx, top_share_val, top_share_qty, n_suppliers]
      const bk = r[1];
      if (!agg[bk]) agg[bk] = { ci: r[2], val: 0, qty: 0, shp: 0, sups: {}, supSet: new Set() };
      agg[bk].val += r[3];
      agg[bk].qty += r[4];
      agg[bk].shp += r[5];
      agg[bk].supSet.add(r[6]);
      const sk = r[6];
      if (!agg[bk].sups[sk]) agg[bk].sups[sk] = { val: 0, qty: 0 };
      agg[bk].sups[sk].val += r[3] * (r[7] / 100);
      agg[bk].sups[sk].qty += r[4] * (r[8] / 100);
    });

    let tv = 0, tq = 0;
    const cset = new Set();
    const cAgg = {};
    const bList = [];

    Object.keys(agg).forEach(bk => {
      const b = agg[bk];
      const bIdx = parseInt(bk);
      const name = L.buyer[bIdx] || '';
      const country = L.country[b.ci] || '';
      cset.add(country);
      tv += b.val;
      tq += b.qty;

      let topSup = '', topVal = 0;
      const nSups = Object.keys(b.sups).length;
      Object.keys(b.sups).forEach(sk => {
        if (b.sups[sk].val > topVal) { topVal = b.sups[sk].val; topSup = L.supplier[parseInt(sk)] || ''; }
      });
      const shareVal = b.val > 0 ? Math.min(100, Math.round(topVal / b.val * 100)) : 0;

      bList.push({ name, country, val: b.val, qty: b.qty, shp: b.shp, topSup, shareVal, nSups: b.supSet.size });

      if (!cAgg[country]) cAgg[country] = { name: country, val: 0, qty: 0, buyers: 0 };
      cAgg[country].val += b.val;
      cAgg[country].qty += b.qty;
      cAgg[country].buyers++;
    });

    const cd = Object.values(cAgg).sort((a, b) => b.val - a.val);

    return { buyers: bList, totalVal: tv, totalQty: tq, countries: cset.size, countryData: cd };
  }, [rawData, appliedRange]);

  // Filter + sort
  const displayBuyers = useMemo(() => {
    let list = [...buyers];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(b => b.name.toLowerCase().includes(term));
    }
    if (filterCountry) {
      list = list.filter(b => b.country === filterCountry);
    }
    if (sortMode === 'val') list.sort((a, b) => b.val - a.val);
    else if (sortMode === 'qty') list.sort((a, b) => b.qty - a.qty);
    else list.sort((a, b) => b.nSups - a.nSups);

    return list.slice(0, 200);
  }, [buyers, searchTerm, filterCountry, sortMode]);

  const visibleBuyers = displayBuyers.slice(0, visibleBuyerCount);

  // Measure how many cards actually fit per row (auto-fill grid), once the grid exists and on resize.
  // Gated on `loading`/list-length rather than `[]` since the grid only mounts after the initial fetch resolves.
  useEffect(() => {
    if (loading) return undefined;
    const grid = buyerGridRef.current;
    if (!grid) return undefined;
    const measureCols = () => {
      const track = window.getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean);
      setColsPerRow(track.length || 1);
    };
    measureCols();
    window.addEventListener('resize', measureCols);
    return () => window.removeEventListener('resize', measureCols);
  }, [loading, displayBuyers.length]);

  // Re-collapse to the first 3 rows whenever the filtered/sorted list or column count changes.
  useEffect(() => {
    setVisibleBuyerCount(buyerPageSize);
    clearTimeout(buyerLoadTimeoutRef.current);
    buyerIsLoadingRef.current = false;
    setBuyerLoadingMore(false);
  }, [displayBuyers, buyerPageSize]);

  // Lock in the scroll panel's height from the real rendered height of the first 3 rows,
  // so the panel scrolls internally instead of the whole page growing.
  useEffect(() => {
    if (buyerScrollH != null) return;
    const grid = buyerGridRef.current;
    if (!grid || grid.children.length === 0) return;
    const rowsToShow = Math.min(buyerPageSize, displayBuyers.length);
    const lastCard = grid.children[rowsToShow - 1];
    if (!lastCard) return;
    const gridTop = grid.getBoundingClientRect().top;
    const lastBottom = lastCard.getBoundingClientRect().bottom;
    setBuyerScrollH(Math.ceil(lastBottom - gridTop));
  }, [visibleBuyers, displayBuyers.length, buyerScrollH, buyerPageSize]);

  // Infinite scroll within the panel: grow visibleBuyerCount as the sentinel scrolls into view
  // inside the bounded scroll container. Data is already fully in memory (client-side aggregation),
  // so a short artificial delay + spinner is added to make the "loading" feel real.
  useEffect(() => {
    if (buyerScrollH == null) return undefined;
    if (visibleBuyerCount >= displayBuyers.length) return undefined;
    const el = buyerSentinelRef.current;
    const root = buyerScrollRef.current;
    if (!el || !root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !buyerIsLoadingRef.current) {
        buyerIsLoadingRef.current = true;
        setBuyerLoadingMore(true);
        buyerLoadTimeoutRef.current = setTimeout(() => {
          setVisibleBuyerCount((v) => Math.min(v + buyerPageSize, displayBuyers.length));
          setBuyerLoadingMore(false);
          buyerIsLoadingRef.current = false;
        }, 500);
      }
    }, { root, rootMargin: '150px' });
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(buyerLoadTimeoutRef.current);
    };
  }, [visibleBuyerCount, displayBuyers.length, buyerScrollH, buyerPageSize]);

  const handleCountryClick = useCallback((name) => {
    setFilterCountry(prev => prev === name ? null : name);
  }, []);

  if (loading) return <BuyersSkeleton />;
  if (error) return <div className="h-full bg-dash-bg flex items-center justify-center"><div className="text-dash-coral font-mono text-sm">Error: {error}</div></div>;

  const kpiCards = [
    { label: 'Buyers', value: buyers.length.toLocaleString(), color: 'text-dash-ice' },
    { label: 'Countries', value: countries, color: 'text-dash-amber' },
    { label: 'Import Value', value: fmtV(totalVal), color: 'text-dash-coral' },
    { label: 'Volume', value: fmtQ(totalQty), color: 'text-[#8FD694]' },
  ];

  const periodLabel = isFiltered
    ? `${months[appliedRange[0]]} to ${months[appliedRange[1]]} (${appliedRange[1] - appliedRange[0] + 1} month${appliedRange[1] - appliedRange[0] === 0 ? '' : 's'})`
    : `All ${months.length || 23} months · ${months[0]} to ${months[months.length - 1]}`;

  return (
    <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="buyers-explorer-react">
      <div className="max-w-[1340px] mx-auto">

        {/* Header */}
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-dash-ice mb-2">Buyers Explorer</div>
        <h1 className="font-grotesk font-extrabold text-[26px] tracking-tight mb-1">Import Buyers — Supplier Concentration Analysis</h1>
        <p className="text-dash-mut text-[13px] mb-4">Click a card for details. Gauge = top supplier&rsquo;s share. Badge = supplier diversity.</p>

        {/* Period Filter */}
        {months.length > 0 && (
          <div className="mb-4">
            <PeriodFilter
              months={months}
              fromIdx={fromIdx}
              toIdx={toIdx}
              onFromChange={setFromIdx}
              onToChange={setToIdx}
              onApply={applyPeriod}
              onReset={resetPeriod}
              periodLabel={periodLabel}
              isFiltered={isFiltered}
            />
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-3.5 mb-5" data-testid="kpi-cards-buyers">
          {kpiCards.map((k, i) => (
            <div key={i} className="bg-dash-panel border border-dash-line rounded-xl p-4 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-dash-mut mb-1.5">{k.label}</div>
              <div className={`font-grotesk font-extrabold text-[28px] ${k.color}`}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* World Map */}
        <div className="bg-dash-panel border border-dash-line rounded-[14px] p-5 mb-4">
          <div className="font-grotesk font-bold text-base mb-1">Destination countries</div>
          <div className="text-dash-mut text-xs mb-3">Bubble size = import value. Click a country to filter buyers below.</div>
          <WorldMap
            countryData={countryData}
            metric="val"
            selectedCountry={filterCountry}
            onCountryClick={handleCountryClick}
          />
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap mb-4" data-testid="buyers-toolbar">
          <input
            type="text"
            placeholder="Search buyer name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="font-sans text-[13px] bg-dash-panel text-dash-ink border border-dash-line rounded-lg px-3.5 py-2.5 w-72 placeholder:text-[#5C7980] focus:outline-none focus:border-dash-ice"
            data-testid="buyer-search"
          />
          {filterCountry && (
            <button
              onClick={() => setFilterCountry(null)}
              className="font-mono text-[11px] text-dash-amber bg-dash-panel border border-dash-amber rounded-lg px-3 py-2 flex items-center gap-1.5 cursor-pointer"
              data-testid="clear-country-filter"
            >
              {filterCountry} <span className="text-dash-coral font-bold">&#10005;</span>
            </button>
          )}
          <div className="flex gap-1 ml-auto" data-testid="sort-buttons">
            {[
              { key: 'val', label: 'By Value $' },
              { key: 'qty', label: 'By Volume' },
              { key: 'sup', label: 'By Suppliers' },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setSortMode(s.key)}
                className={`font-mono text-[10px] px-3 py-2 rounded-md border cursor-pointer transition ${
                  sortMode === s.key
                    ? 'bg-dash-ice text-[#062023] border-dash-ice font-bold'
                    : 'bg-dash-panel text-dash-mut border-dash-line hover:text-dash-ink'
                }`}
                data-testid={`sort-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <span className="font-mono text-[11px] text-dash-mut ml-2" data-testid="buyer-count">
            Showing {displayBuyers.length} of {buyers.length} buyers
          </span>
        </div>

        {/* Buyer Cards Grid */}
        {displayBuyers.length > 0 ? (
          <div
            ref={buyerScrollRef}
            className="overflow-y-auto dash-scrollbar pr-3"
            style={buyerScrollH != null ? { maxHeight: `${buyerScrollH}px` } : undefined}
            data-testid="buyer-scroll-container"
          >
            <div ref={buyerGridRef} className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-3.5" data-testid="buyer-grid">
              {visibleBuyers.map((b, i) => <BuyerCard key={`${b.country}-${b.name}-${i}`} buyer={b} />)}

              {buyerLoadingMore && Array.from({ length: Math.min(buyerPageSize, displayBuyers.length - visibleBuyerCount) }).map((_, i) => (
                <BuyerCardSkeleton key={`skeleton-${i}`} delay={i * 30} />
              ))}
            </div>

            {visibleBuyerCount < displayBuyers.length && (
              <div ref={buyerSentinelRef} className="h-px" data-testid="buyer-grid-sentinel" />
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-dash-mut font-mono text-sm" data-testid="buyer-grid">No buyers found</div>
        )}

        {/* Hint */}
        <div className="mt-6 text-[11px] text-[#5C7980] border-t border-dash-line pt-3.5">
          Gauge = top supplier&rsquo;s share of that buyer (by $). Badge: green = single source, amber = 2-3 suppliers, coral = 4+ suppliers (multi-sourced).
        </div>

        <FloatingFAB guideContent={GUIDE} />
      </div>
    </div>
  );
}
